import type { CSSProperties, ReactNode } from 'react'

/**
 * Loading skeletons (docs/UX-PLAYBOOK.md §7 "loading skeleton", §8): grey blocks in the shape of what is coming, with a
 * slow shimmer that stops under reduced motion. The wrapper announces the loading state once (`aria-busy` + label);
 * the blocks themselves are hidden from assistive tech.
 */
type Len = number | string

export function Sk({ w = '100%', h = 14, r = 8, style, className = '' }: { w?: Len; h?: Len; r?: Len; style?: CSSProperties; className?: string }) {
  return <span className={`sk ${className}`} aria-hidden="true" style={{ width: w, height: h, borderRadius: r, ...style }} />
}

/** Without `label` it is a plain block, for a skeleton inside a region that already announces the loading. */
export function Busy({ label, className = '', style, children }: { label?: string; className?: string; style?: CSSProperties; children: ReactNode }) {
  return label ? <div className={className} style={style} aria-busy="true" aria-label={label}>{children}</div> : <div className={className} style={style}>{children}</div>
}

const LINE_W = ['100%', '94%', '82%', '68%', '88%']

/** A card: pill, optional Arabic lines (right-aligned), then text lines. */
export function SkCard({ label, lines = 3, ar = false, h, className = '', style }: { label?: string; lines?: number; ar?: boolean; h?: Len; className?: string; style?: CSSProperties }) {
  return (
    <Busy label={label} className={`card sk-card ${className}`} style={{ minHeight: h, ...style }}>
      <Sk w={110} h={24} r={999} />
      {ar && <div className="sk-ar"><Sk w="92%" h={28} /><Sk w="64%" h={28} /></div>}
      <div className="sk-lines">{Array.from({ length: lines }, (_, i) => <Sk key={i} w={LINE_W[i % LINE_W.length]} />)}</div>
    </Busy>
  )
}

/** A list of rows (number badge, title + subtitle, trailing block): surah list, saved items. */
export function SkRows({ label, rows = 8, className = '' }: { label: string; rows?: number; className?: string }) {
  return (
    <Busy label={label} className={`card sk-rows ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="sk-row">
          <Sk w={38} h={38} r={12} />
          <div className="sk-col"><Sk w={`${48 + ((i * 17) % 30)}%`} h={14} /><Sk w={`${30 + ((i * 11) % 20)}%`} h={11} /></div>
          <Sk w={52} h={22} />
        </div>
      ))}
    </Busy>
  )
}

/** Verse cards of the reader. */
export function SkVerses({ label, count = 2 }: { label: string; count?: number }) {
  return (
    <Busy label={label} className="stack sk-verses">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="card sk-card">
          <div className="sk-head"><Sk w={58} h={24} r={999} /><Sk w={40} h={40} r={999} /></div>
          <div className="sk-ar"><Sk w="96%" h={30} /><Sk w="72%" h={30} /></div>
          <div className="sk-lines"><Sk /><Sk w="86%" /><Sk w="58%" /></div>
        </div>
      ))}
    </Busy>
  )
}

/** Perjalanan hero: the square garden picture + a few lines. */
export function SkGarden({ label }: { label: string }) {
  return (
    <Busy label={label} className="card garden sk-garden">
      <Sk h="auto" r={0} style={{ aspectRatio: '1' }} />
      <div className="garden-body sk-lines"><Sk w={140} h={12} /><Sk w="60%" h={22} /><Sk h={10} r={999} /><Sk w="76%" /></div>
    </Busy>
  )
}
